/**
 * The two removals on the calendar page that may have to ask a follow-up
 * question — "the calendar still has events, delete them too?" and "also delete
 * the events this feed added?" — lifted out of CalendarMainPage so the asking
 * and the answer handling can be tested without mounting the whole page.
 *
 * The questions go through the in-app `useConfirm()`; see
 * components/ui/confirm-dialog.tsx for why never `window.confirm`.
 */
import { useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { useConfirm } from '@/components/ui/confirm-dialog';

export interface CalendarRemovalPromptsInput {
  /** The calendars currently loaded, to name the one being deleted. */
  calendars: ReadonlyArray<{ id: string; name: string }>;
  /** Deletes a calendar; `deleteEvents` cascades to its events. */
  deleteCalendar: (id: string, deleteEvents?: boolean) => Promise<void>;
  /** Removes a feed subscription, keeping or removing the events it imported. */
  unsubscribe: (id: string, mode: 'keep' | 'remove') => Promise<{ removed?: number } | void>;
  fetchCalendars: () => Promise<void> | void;
  refetchEvents: () => Promise<unknown> | void;
}

export interface CalendarRemovalPrompts {
  handleDeleteCalendar: (id: string) => Promise<void>;
  handleUnsubscribe: (id: string) => Promise<void>;
}

export function useCalendarRemovalPrompts({
  calendars,
  deleteCalendar,
  unsubscribe,
  fetchCalendars,
  refetchEvents,
}: CalendarRemovalPromptsInput): CalendarRemovalPrompts {
  const confirm = useConfirm();

  const handleDeleteCalendar = useCallback(
    async (id: string) => {
      try {
        await deleteCalendar(id);
        toast({ title: 'Calendar deleted' });
        void refetchEvents();
      } catch (err) {
        // The non-empty guard is a safety check, not a dead end: offer the
        // cascade rather than leaving the calendar permanently undeletable.
        const message = err instanceof Error ? err.message : 'Failed to delete calendar';
        if (message.includes('CALENDAR_NOT_EMPTY')) {
          const name = calendars.find((c) => c.id === id)?.name ?? 'this calendar';
          if (
            await confirm({
              title: `"${name}" still has events. Delete them too?`,
              description:
                'Deleting removes the calendar AND its events. Keeping leaves everything.',
              confirmLabel: 'Delete calendar and events',
              cancelLabel: 'Keep everything',
              destructive: true,
            })
          ) {
            try {
              await deleteCalendar(id, true);
              toast({ title: 'Calendar and its events deleted' });
              void refetchEvents();
            } catch (cascadeErr) {
              toast({
                title: 'Error',
                description:
                  cascadeErr instanceof Error ? cascadeErr.message : 'Failed to delete calendar',
                variant: 'destructive',
              });
            }
          }
          return;
        }
        toast({ title: 'Error', description: message, variant: 'destructive' });
      }
    },
    [deleteCalendar, refetchEvents, calendars, confirm],
  );

  const handleUnsubscribe = useCallback(
    async (id: string) => {
      try {
        // Unsubscribing used to strand every event the feed imported, and the
        // calendar then refused to delete (CALENDAR_NOT_EMPTY) — no cleanup
        // path existed. Ask, the same way disconnect does.
        let removable = 0;
        try {
          removable = (await api.calendarSubscriptionDeletePreview(id)).removable;
        } catch {
          // Preview is best-effort — fall through to a plain unsubscribe.
        }
        const remove =
          removable > 0 &&
          (await confirm({
            title: `Also delete the ${removable} event(s) this feed added?`,
            description: 'Keeping them leaves the events in the calendar.',
            confirmLabel: 'Delete them',
            cancelLabel: 'Keep them',
            destructive: true,
          }));
        const res = await unsubscribe(id, remove ? 'remove' : 'keep');
        toast({
          title: 'Unsubscribed',
          description: res?.removed ? `${res.removed} event(s) removed.` : undefined,
        });
        void fetchCalendars();
        void refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to unsubscribe',
          variant: 'destructive',
        });
      }
    },
    [unsubscribe, fetchCalendars, refetchEvents, confirm],
  );

  return { handleDeleteCalendar, handleUnsubscribe };
}
